import React from "react";
import { commitMutation, graphql, useFragment } from "react-relay";

import { compact } from "lodash";

import { BillingContext } from "~/account/Billing/BillingContext";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { eventEmitter as recordingsEventEmitter } from "~/recordings/events";
import { Button, toast } from "~/ui";
import { SimpleInput } from "~/ui/Input";
import { Modal } from "~/ui/legacyFellowIndex";
import Selector from "~/ui/selectors/Selector";

import { NoteDate } from "../NoteHeader/NoteHeader";

import type { DeleteNoteModal_note$data, DeleteNoteModal_note$key } from "~/__generated__/DeleteNoteModal_note.graphql";
import type { DeleteNoteModalDeleteNoteMutation as DeleteNoteModalDeleteNoteMutationType } from "~/__generated__/DeleteNoteModalDeleteNoteMutation.graphql";
import type { DeleteNoteModalDeleteRecordingMutation as DeleteNoteModalDeleteRecordingMutationType } from "~/__generated__/DeleteNoteModalDeleteRecordingMutation.graphql";
import type { DeleteNoteModalDeleteTranscriptAndVideoMutation as DeleteNoteModalDeleteTranscriptAndVideoMutationType } from "~/__generated__/DeleteNoteModalDeleteTranscriptAndVideoMutation.graphql";
import type { DeleteNoteModalDeleteVideoMutation as DeleteNoteModalDeleteVideoMutationType } from "~/__generated__/DeleteNoteModalDeleteVideoMutation.graphql";

import styles from "./DeleteNoteModal.module.css";

const deleteMutation = graphql`
	mutation DeleteNoteModalDeleteNoteMutation($input: DeleteNoteInput!) {
		deleteNote(input: $input) {
			clientMutationId
			ok
			noteId
		}
	}
`;

const deleteRecordingMutation = graphql`
	mutation DeleteNoteModalDeleteRecordingMutation($recordingId: ID!) {
		deleteRecording(recordingId: $recordingId) {
			note {
				id
				hasMeetingRecordings
				meetingRecordings {
					id
				}
			}
		}
	}
`;

const deleteTranscriptAndVideoMutation = graphql`
	mutation DeleteNoteModalDeleteTranscriptAndVideoMutation($recordingId: ID!) {
		deleteMeetingRecordingTranscriptAndVideo(meetingRecordingId: $recordingId) {
			meetingRecording {
				id
				transcript
				transcriptDeletionReason
				meetingRecordedVideoUrl
				meetingRecordedVideoPosterUrl
				meetingRecordedVideoDuration
				meetingRecordingVideoDeletionReason
			}
		}
	}
`;

const deleteVideoMutation = graphql`
	mutation DeleteNoteModalDeleteVideoMutation($recordingId: ID!) {
		deleteMeetingRecordingVideo(meetingRecordingId: $recordingId) {
			meetingRecording {
				id
				transcript
				transcriptDeletionReason
				meetingRecordedVideoUrl
				meetingRecordedVideoPosterUrl
				meetingRecordedVideoDuration
				meetingRecordingVideoDeletionReason
			}
		}
	}
`;

export type DeletionType = "EVERYTHING" | "RECAP" | "TRANSCRIPT_AND_VIDEO" | "VIDEO";

// Note info component to display note details
const NoteInfo = ({ note }: { note: DeleteNoteModal_note$data }) => {
	const noteDateEvent =
		note.eventStart && note.eventEnd
			? { start: new Date(note.eventStart), end: new Date(note.eventEnd), isAllDay: false }
			: null;

	const noteDateCreatedAt = note.createdAt ? new Date(note.createdAt) : null;

	return (
		<div className={styles.infoContainer}>
			<div className={styles.noteInfoContainer}>
				<p className={styles.title} title={note.title}>
					{note.title}
				</p>
				<span className={styles.separator} />
				<NoteDate createdAt={noteDateCreatedAt} isCancelled={false} event={noteDateEvent} />
			</div>
		</div>
	);
};

// Confirmation input component
const ConfirmationInput = ({
	confirmText,
	setConfirmText,
}: {
	confirmText: string;
	setConfirmText: (value: string) => void;
}) => (
	<div className={styles.controlsGroup}>
		<div className={styles.inputLabel}>Please type &apos;DELETE&apos; to confirm.</div>
		<SimpleInput value={confirmText} onValueChange={setConfirmText} name="delete_note_confirmation_input" />
	</div>
);

// Simple note deletion modal (no recordings)
const SimpleNoteDeletionModal = ({
	note,
	confirmText,
	setConfirmText,
	onDelete,
	onAbort,
	confirmButtonEnabled,
}: {
	note: DeleteNoteModal_note$data;
	confirmText: string;
	setConfirmText: (value: string) => void;
	onDelete: () => void;
	onAbort: () => void;
	confirmButtonEnabled: boolean;
}) => (
	<>
		<NoteInfo note={note} />

		<div className={styles.deleteModalContent}>
			The note and its contents will be <span className={styles.warningText}>permanently deleted.</span>
		</div>

		<div className={styles.controlsGroup}>
			<div className={styles.deleteModalContent}>Proceed? This action cannot be undone.</div>
		</div>

		<ConfirmationInput confirmText={confirmText} setConfirmText={setConfirmText} />

		<Modal.Footer>
			<Button variant="secondary" onClick={onAbort}>
				Cancel
			</Button>
			<Button variant="destructive" onClick={onDelete} disabled={!confirmButtonEnabled}>
				Permanently delete
			</Button>
		</Modal.Footer>
	</>
);

// Recording deletion modal (with recordings)
const RecordingDeletionModal = ({
	note,
	deletionType,
	setDeletionType,
	deleteOptions,
	hasClips,
	confirmText,
	setConfirmText,
	onDelete,
	onAbort,
	confirmButtonEnabled,
}: {
	note: DeleteNoteModal_note$data;
	deletionType: DeletionType | null;
	setDeletionType: (type: DeletionType) => void;
	deleteOptions: Array<{ label: string; value: DeletionType }>;
	hasClips: boolean;
	confirmText: string;
	setConfirmText: (value: string) => void;
	onDelete: () => void;
	onAbort: () => void;
	confirmButtonEnabled: boolean;
}) => {
	const onChangeDeletionType = React.useCallback(
		(option: { label: string; value: DeletionType } | null) => {
			if (!option) return;
			setDeletionType(option.value);
		},
		[setDeletionType],
	);

	return (
		<>
			<NoteInfo note={note} />

			<div className={styles.deleteModalHeading}>
				Select what you&apos;d like to delete for this meeting.
				<span className={styles.warningText}> The selected items will be permanently deleted.</span>
			</div>

			<div className={styles.controlsGroup}>
				<div className={styles.inputLabel}>Select item(s) to delete:</div>
				<Selector
					name="deletion_type"
					onChange={onChangeDeletionType}
					options={deleteOptions}
					value={deleteOptions.find(option => option.value === deletionType)}
				/>
				{(deletionType === "RECAP" || deletionType === "EVERYTHING") && hasClips && (
					<div className={styles.inputSubLabel}>Clips from this meeting will also be deleted.</div>
				)}
			</div>

			<ConfirmationInput confirmText={confirmText} setConfirmText={setConfirmText} />

			<Modal.Footer>
				<Button variant="secondary" onClick={onAbort}>
					Cancel
				</Button>
				<Button variant="destructive" onClick={onDelete} disabled={!confirmButtonEnabled}>
					Permanently delete
				</Button>
			</Modal.Footer>
		</>
	);
};

export default function DeleteNoteModal({
	onClose: _onClose,
	note: noteKey,
	recordingId,
}: {
	onClose: (type: DeletionType | null) => void;
	note: DeleteNoteModal_note$key;
	recordingId?: string;
}) {
	const note = useFragment(
		graphql`
			fragment DeleteNoteModal_note on Note {
				id
				title
				eventStart
				eventEnd
				createdAt
				meetingRecordings {
					id
					hasTranscript
					meetingRecordedVideoUrl
					clips {
						id
						title
					}
				}
				stream {
					id
				}
			}
		`,
		noteKey,
	);

	const { analyticsFingerprint } = React.useContext(BillingContext);

	// State management
	const [deletionType, setDeletionType] = React.useState<DeletionType | null>(null);
	const [confirmText, setConfirmText] = React.useState("");

	// Derived state
	const hasRecordings = note?.meetingRecordings && note?.meetingRecordings.length > 0;
	const deletedRecording = React.useMemo(
		() => note?.meetingRecordings?.find(mr => mr.id === recordingId),
		[note, recordingId],
	);

	// Set default deletion type for simple note deletion
	React.useEffect(() => {
		if (!hasRecordings) {
			setDeletionType("EVERYTHING");
		}
	}, [hasRecordings]);

	// Calculate delete options based on recording capabilities
	const deleteOptions: { label: string; value: DeletionType }[] = React.useMemo(() => {
		let options = [
			{
				label: "Note, Recap, Transcript & Video",
				value: "EVERYTHING" as DeletionType,
			},
			{
				label: "Recap, Transcript & Video",
				value: "RECAP" as DeletionType,
			},
		];

		if (deletedRecording?.hasTranscript) {
			options.push({
				label: "Transcript & Video",
				value: "TRANSCRIPT_AND_VIDEO",
			});
		}

		if (deletedRecording?.meetingRecordedVideoUrl) {
			options.push({
				label: "Video",
				value: "VIDEO",
			});
		}

		return options;
	}, [deletedRecording]);

	// Calculate if there are clips that would be affected
	const allClips = note?.meetingRecordings
		?.filter(recording => recording.clips !== null)
		.reduce(
			(acc, recording) => {
				if (!recording.clips || (deletionType === "RECAP" && recordingId && recordingId !== recording.id)) return acc;
				return acc.concat(recording.clips);
			},
			[] as { id: string; title: string }[],
		);
	const hasClips = Boolean(allClips && allClips.length > 0);

	const confirmButtonEnabled = confirmText === "DELETE" && !!deletionType;

	const abort = React.useCallback(() => _onClose(null), [_onClose]);
	const proceed = React.useCallback(() => _onClose(deletionType), [_onClose, deletionType]);

	const deleteNote = React.useCallback(() => {
		commitMutation<DeleteNoteModalDeleteNoteMutationType>(environment, {
			mutation: deleteMutation,
			variables: { input: { noteId: note.id } },
			updater: (store, response) => {
				if (!response) return;
				if (!response.deleteNote || !response.deleteNote.noteId) return;

				const streamId = note.stream.id;
				store.delete(response.deleteNote.noteId);

				const stream = store.get(streamId);
				if (!stream) return;

				const allNotes = stream.getLinkedRecords("allNotes");
				if (!allNotes) return;
				stream.setLinkedRecords(compact(allNotes), "allNotes");
			},
			onCompleted: (response, errors) => {
				if (errors) {
					toast.error("Failed to delete recording");
					return;
				}
				dispatchAnalyticsTrack("[AI Copilot] Deleted All Recordings and Note", { fingerprint: analyticsFingerprint });
				proceed();
				recordingsEventEmitter.emit("Recordings.refetch");
				toast.success("Deleted Note");
			},
		});
	}, [analyticsFingerprint, note, proceed]);

	const deleteRecording = React.useCallback(() => {
		if (!recordingId) {
			return;
		}

		commitMutation<DeleteNoteModalDeleteRecordingMutationType>(environment, {
			mutation: deleteRecordingMutation,
			variables: { recordingId },
			onCompleted: (_response, errors) => {
				if (errors) {
					toast.error("Failed to delete recording");
					return;
				}
				dispatchAnalyticsTrack("[AI Copilot] Deleted recording", { fingerprint: analyticsFingerprint });
				proceed();
				recordingsEventEmitter.emit("Recordings.refetch");
				toast.success("Deleted recap");
			},
		});
	}, [analyticsFingerprint, proceed, recordingId]);

	const deleteTranscriptAndVideo = React.useCallback(() => {
		if (!recordingId) {
			return;
		}

		commitMutation<DeleteNoteModalDeleteTranscriptAndVideoMutationType>(environment, {
			mutation: deleteTranscriptAndVideoMutation,
			variables: { recordingId },
			onCompleted: (_response, errors) => {
				if (errors) {
					toast.error("Failed to delete transcript and video");
					return;
				}
				dispatchAnalyticsTrack("[AI Copilot] Deleted video and transcript", { fingerprint: analyticsFingerprint });
				proceed();
				toast.success("Deleted transcript and video");
			},
		});
	}, [analyticsFingerprint, proceed, recordingId]);

	const deleteVideo = React.useCallback(() => {
		if (!recordingId) {
			return;
		}

		commitMutation<DeleteNoteModalDeleteVideoMutationType>(environment, {
			mutation: deleteVideoMutation,
			variables: { recordingId },
			onCompleted: (_response, errors) => {
				if (errors) {
					toast.error("Failed to delete video");
					return;
				}
				dispatchAnalyticsTrack("[AI Copilot] Deleted video", { fingerprint: analyticsFingerprint });
				proceed();
				toast.success("Deleted video");
			},
		});
	}, [analyticsFingerprint, proceed, recordingId]);

	const onDelete = React.useCallback(() => {
		switch (deletionType) {
			case "EVERYTHING":
				return deleteNote();

			case "RECAP":
				return deleteRecording();

			case "TRANSCRIPT_AND_VIDEO":
				return deleteTranscriptAndVideo();

			case "VIDEO":
				return deleteVideo();
		}
	}, [deletionType, deleteNote, deleteRecording, deleteTranscriptAndVideo, deleteVideo]);

	if (!note) return null;

	const modalTitle = hasRecordings ? "Delete…" : "Delete Note?";

	return (
		<Modal onClose={abort}>
			<Modal.Header onClose={abort} noBottomBorder>
				<Modal.Title>{modalTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				{hasRecordings ? (
					<RecordingDeletionModal
						note={note}
						deletionType={deletionType}
						setDeletionType={setDeletionType}
						deleteOptions={deleteOptions}
						hasClips={hasClips}
						confirmText={confirmText}
						setConfirmText={setConfirmText}
						onDelete={onDelete}
						onAbort={abort}
						confirmButtonEnabled={confirmButtonEnabled}
					/>
				) : (
					<SimpleNoteDeletionModal
						note={note}
						confirmText={confirmText}
						setConfirmText={setConfirmText}
						onDelete={onDelete}
						onAbort={abort}
						confirmButtonEnabled={confirmButtonEnabled}
					/>
				)}
			</Modal.Body>
		</Modal>
	);
}
